import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../services/api';
import { toast } from 'sonner';
import { Mail, Building, Briefcase, Clock } from 'lucide-react';

const AdminContacts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchContacts();
  }, [user, navigate, statusFilter]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const res = await api.get(`/admin/contacts?${params.toString()}`);
      setContacts(res.data.contacts);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, status) => {
    try {
      await api.post(`/admin/contacts/${contactId}/status?status=${status}`);
      toast.success('Status updated');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-[rgba(27,90,255,0.15)] text-[#1B5AFF]',
      read: 'bg-[rgba(91,100,114,0.15)] text-[#5B6472]',
      replied: 'bg-[rgba(0,201,167,0.15)] text-[#00C9A7]',
      archived: 'bg-[rgba(240,180,41,0.15)] text-[#F0B429]'
    };
    return <span className={`px-2 py-1 rounded text-xs font-mono ${styles[status] || styles.new}`}>{status}</span>;
  };

  const filterActions = (
    <Select value={statusFilter} onValueChange={setStatusFilter}>
      <SelectTrigger className="w-[150px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
        <SelectValue placeholder="Status" />
      </SelectTrigger>
      <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
        <SelectItem value="all" className="text-[#0A0F1E]">All Status</SelectItem>
        <SelectItem value="new" className="text-[#0A0F1E]">New</SelectItem>
        <SelectItem value="read" className="text-[#0A0F1E]">Read</SelectItem>
        <SelectItem value="replied" className="text-[#0A0F1E]">Replied</SelectItem>
        <SelectItem value="archived" className="text-[#0A0F1E]">Archived</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <AdminLayout 
      title="Contact Submissions" 
      breadcrumbs={[{ label: 'Contacts' }]}
      actions={filterActions}
    >
      <p className="text-[#5B6472] mb-6">{total} total submissions</p>

      {/* Contacts List */}
      <div className="space-y-4">
        {contacts.map((contact) => (
          <Card key={contact.id} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#1B5AFF]" />
                </div>
                <div>
                  <p className="text-[#0A0F1E] font-medium">{contact.name}</p>
                  <p className="text-[#5B6472] text-sm">{contact.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(contact.status)}
                <span className="text-[#5B6472] text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date(contact.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            
            <div className="flex gap-4 mb-4 text-sm">
              {contact.company && (
                <span className="text-[#5B6472] flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {contact.company}
                </span>
              )}
              <span className="text-[#5B6472] flex items-center gap-1">
                {contact.account_type === 'employer' 
                  ? <Building className="w-4 h-4" />
                  : <Briefcase className="w-4 h-4" />
                }
                {contact.account_type}
              </span>
            </div>

            <div className="bg-[#F5F8FF]/50 rounded-lg p-4 mb-4">
              <p className="text-[#1B5AFF] text-sm font-medium mb-2">{contact.subject}</p>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{contact.message}</p>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => updateStatus(contact.id, 'read')}
                className="text-[#5B6472] hover:text-[#0A0F1E]"
              >
                Mark Read
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => updateStatus(contact.id, 'replied')}
                className="text-[#00C9A7] hover:text-[#00967A]"
              >
                Mark Replied
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => updateStatus(contact.id, 'archived')}
                className="text-[#F0B429] hover:text-[#D99E22]"
              >
                Archive
              </Button>
              <a 
                href={`mailto:${contact.email}?subject=Re: ${contact.subject}`}
                className="ml-auto"
              >
                <Button
                  size="sm"
                  className="bg-[#1B5AFF] hover:bg-[#144BD6] text-[#FFFFFF]"
                >
                  Reply via Email
                </Button>
              </a>
            </div>
          </Card>
        ))}

        {contacts.length === 0 && !loading && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
            <Mail className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <p className="text-[#5B6472]">No contact submissions found</p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
